import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { VehicleSummaryFE } from 'domain/vehicle';
import type { TeamFormData } from 'features/settings/teams/types';
import type { UserFormData } from 'features/settings/users/types';
import type { SystemAdminDialogTypes } from 'helper/types';
import type { RootState } from 'store/reducers';

import type { SystemFleet, SystemTeam, User } from 'API';

export enum ActiveSidebar {
    USERS = 'USERS',
    TEAMS = 'TEAMS',
    FLEETS = 'FLEETS',
    VEHICLES = 'VEHICLES',
}

export type ActiveSidebarT =
    | ActiveSidebar.USERS
    | ActiveSidebar.TEAMS
    | ActiveSidebar.FLEETS
    | ActiveSidebar.VEHICLES
    | undefined;

export interface UserDialogState {
    user: undefined | User;
    showUserDialog: boolean;
}

export interface TeamDialogState {
    team?: undefined | SystemTeam;
    showTeamDialog: boolean;
}

export type VehicleDialogState =
    | { status: 'closed' }
    | { status: 'open'; mode: 'create' }
    | { status: 'open'; mode: 'update'; vehicle: VehicleSummaryFE };

export interface SettingsPageState {
    activeSidebar: ActiveSidebarT;
    activeUser: undefined | User;
    selectedTeam: undefined | SystemTeam;
    selectedFleet: undefined | SystemFleet;
    selectedVehicleVin?: string;
    showRoleDialog: boolean;
    vehicleDialog: VehicleDialogState;
    showExitActionDialog: boolean;
    showExitTeamActionDialog: boolean;
    errorDialogOpen: boolean;
    userDialog: UserDialogState;
    teamDialog: TeamDialogState;
    teamFormData?: TeamFormData;
    userFormData?: UserFormData;
    openDialog: SystemAdminDialogTypes | undefined;
}

const initialState: SettingsPageState = {
    activeSidebar: undefined,
    activeUser: undefined,
    selectedTeam: undefined,
    selectedFleet: undefined,
    selectedVehicleVin: undefined,
    showRoleDialog: false,
    vehicleDialog: { status: 'closed' },
    showExitActionDialog: false,
    showExitTeamActionDialog: false,
    errorDialogOpen: false,
    userDialog: {
        user: undefined,
        showUserDialog: false,
    },
    teamDialog: {
        team: undefined,
        showTeamDialog: false,
    },
    userFormData: undefined,
    teamFormData: undefined,
    openDialog: undefined,
};

const clearSelectedElements = (state: SettingsPageState) => {
    state.activeUser = undefined;
    state.selectedTeam = undefined;
    state.selectedFleet = undefined;
    state.selectedVehicleVin = undefined;
};

export const settingsPageSlice = createSlice({
    name: 'settingsPage',
    initialState,
    reducers: {
        setActiveUser: (state: SettingsPageState, action: PayloadAction<User | undefined>) => {
            clearSelectedElements(state);
            state.activeUser = action.payload;
        },
        setSelectedTeam: (state: SettingsPageState, action: PayloadAction<SystemTeam | undefined>) => {
            clearSelectedElements(state);
            state.activeSidebar = ActiveSidebar.TEAMS;
            state.selectedTeam = action.payload;
        },
        setSelectedFleet: (state: SettingsPageState, action: PayloadAction<SystemFleet | undefined>) => {
            clearSelectedElements(state);
            state.selectedFleet = action.payload;
        },
        setSelectedVehicleVin: (state: SettingsPageState, action: PayloadAction<string | undefined>) => {
            clearSelectedElements(state);
            state.activeSidebar = ActiveSidebar.VEHICLES;
            state.selectedVehicleVin = action.payload;
        },
        setActiveSidebar: (state: SettingsPageState, action: PayloadAction<ActiveSidebarT | undefined>) => {
            state.activeSidebar = action.payload;
        },
        clearActiveElements: (state: SettingsPageState) => {
            state.activeSidebar = undefined;
            clearSelectedElements(state);
        },
        toogleShowRoleDialog: (state: SettingsPageState) => {
            state.showRoleDialog = !state.showRoleDialog;
        },
        setVehicleDialog: (state: SettingsPageState, action: PayloadAction<VehicleDialogState>) => {
            state.vehicleDialog = action.payload;
        },
        setExitActionDialog: (state: SettingsPageState, action: PayloadAction<boolean>) => {
            state.showExitActionDialog = action.payload;
        },
        setExitTeamActionDialog: (state: SettingsPageState, action: PayloadAction<boolean>) => {
            state.showExitTeamActionDialog = action.payload;
        },
        setErrorDialogOpen: (state: SettingsPageState, action: PayloadAction<boolean>) => {
            state.errorDialogOpen = action.payload;
        },
        setUserDialog: (state: SettingsPageState, action: PayloadAction<UserDialogState>) => {
            state.userDialog.user = action.payload.user ?? undefined;
            state.userDialog.showUserDialog = action.payload.showUserDialog;
        },
        setTeamDialog: (state: SettingsPageState, action: PayloadAction<TeamDialogState>) => {
            state.teamDialog.team = action.payload.team ?? ({} as SystemTeam);
            state.teamDialog.showTeamDialog = action.payload.showTeamDialog;
        },
        setUserFormData: (state: SettingsPageState, action: PayloadAction<UserFormData | undefined>) => {
            state.userFormData = action.payload;
        },
        setTeamFormData: (state: SettingsPageState, action: PayloadAction<TeamFormData | undefined>) => {
            state.teamFormData = action.payload;
        },
        setSystemAdminOpenDialog: (
            state: SettingsPageState,
            action: PayloadAction<SystemAdminDialogTypes | undefined>
        ) => {
            state.openDialog = action.payload;
        },
    },
});

export const {
    setActiveUser,
    setSelectedTeam,
    setSelectedFleet,
    setSelectedVehicleVin,
    clearActiveElements,
    toogleShowRoleDialog,
    setVehicleDialog,
    setUserDialog,
    setExitActionDialog,
    setExitTeamActionDialog,
    setErrorDialogOpen,
    setSystemAdminOpenDialog,
    setActiveSidebar,
    setTeamDialog,
    setTeamFormData,
    setUserFormData,
} = settingsPageSlice.actions;

export const getActiveSidebar = (state: RootState) => state.settingsPage.activeSidebar;
export const getActiveUser = (state: RootState) => state.settingsPage.activeUser;
export const getSelectedTeam = (state: RootState) => state.settingsPage.selectedTeam;
export const getSelectedFleet = (state: RootState) => state.settingsPage.selectedFleet;
export const getSelectedVehicleVin = (state: RootState) => state.settingsPage.selectedVehicleVin;
export const getShowRoleDialog = (state: RootState) => state.settingsPage.showRoleDialog;
export const getVehicleDialog = (state: RootState) => state.settingsPage.vehicleDialog;
export const getUserDialog = (state: RootState) => state.settingsPage.userDialog;
export const getTeamDialog = (state: RootState) => state.settingsPage.teamDialog;
export const getShowExitActionDialog = (state: RootState) => state.settingsPage.showExitActionDialog;
export const getShowExitTeamActionDialog = (state: RootState) => state.settingsPage.showExitTeamActionDialog;
export const getErrorDialogOpen = (state: RootState) => state.settingsPage.errorDialogOpen;
export const getSystemAdminOpenDialog = (state: RootState) => state.settingsPage.openDialog;
export const getTeamFormData = (state: RootState) => state.settingsPage.teamFormData;
export const getUserFormData = (state: RootState) => state.settingsPage.userFormData;

export default settingsPageSlice.reducer;
